import { isMachine } from '@fleetcore/shared'
import type { LiveVehicleState } from '@fleetcore/shared/telemetry'
import { Droplets, Fuel, Gauge, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { MetricTile } from '../../components/metric-tile'
import { useFormatters } from '../../i18n/format'
import type { VehicleView } from './api-types'
import {
  channelReading,
  consumptionNormReading,
  fuelLevelReading,
  usageReading,
} from './usage-metrics'

/**
 * The four headline numbers, each of which refuses to be a zero it did not measure.
 *
 * The first tile is the one the class fork exists for: kilometres for a truck,
 * engine hours for an excavator. The reference-consumption tile follows the same
 * fork into L/100 km or L/h. Everything else is CAN-fed and says so when it is not
 * there.
 */
export function UsageTiles({
  vehicle,
  state,
  isLoading = false,
}: {
  vehicle: VehicleView
  state: LiveVehicleState | null
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const machine = isMachine(vehicle)

  const usage = usageReading(vehicle, state)
  const norm = consumptionNormReading(vehicle)
  const fuel = fuelLevelReading(state)
  const rate = channelReading(state, 'fuel_rate')

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricTile
        label={t(usage.labelKey)}
        icon={machine ? Timer : Gauge}
        metric={usage.value}
        isLoading={isLoading}
        format={(value) => `${formatters.decimal(value, usage.precision)} ${t(usage.unitKey)}`}
        hint={t(machine ? 'vehicles:detail.usage.hoursHint' : 'vehicles:detail.usage.odometerHint')}
      />

      <MetricTile
        label={t(norm.labelKey)}
        icon={Fuel}
        metric={norm.value}
        isLoading={isLoading}
        format={(value) => `${formatters.decimal(value, 1)} ${t(norm.unitKey)}`}
        hint={t('vehicles:detail.consumptionNormHint')}
      />

      <MetricTile
        label={t('vehicles:detail.fuelLevel')}
        icon={Droplets}
        metric={fuel.metric}
        isLoading={isLoading}
        format={(value) =>
          fuel.unit === 'litres'
            ? `${formatters.decimal(value, 1)} L`
            : `${formatters.integer(value)} %`
        }
        hint={
          vehicle.tankCapacityL === null
            ? undefined
            : t('vehicles:detail.tankCapacity', {
                litres: formatters.integer(vehicle.tankCapacityL),
              })
        }
      />

      <MetricTile
        label={t('vehicles:detail.fuelRate')}
        icon={Fuel}
        metric={rate}
        isLoading={isLoading}
        format={(value) => `${formatters.decimal(value, 1)} ${t('common:units.lPerHour')}`}
        hint={t('vehicles:detail.fuelRateHint')}
      />
    </div>
  )
}
